export { resolveMediaUrl } from "../../../utils/media";

export const languages = ["en", "ar", "ku"];

export const formatFieldTitle = (raw) =>
  String(raw || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());

export const sanitizePathForDisplay = (rawPath) =>
  String(rawPath || "")
    .split(".")
    .filter((part) => part && !/^\d+$/.test(part))
    .join(".");

export const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

export const parseStructuredValue = (rawValue) => {
  if (isPlainObject(rawValue)) return rawValue;
  if (typeof rawValue !== "string") return null;
  const trimmed = rawValue.trim();
  if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) return null;
  try {
    const parsed = JSON.parse(trimmed);
    return isPlainObject(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const parseMaybeObject = (value) => {
  if (isPlainObject(value)) return value;
  if (typeof value !== "string") return null;
  const parsed = parseStructuredValue(value);
  return isPlainObject(parsed) ? parsed : null;
};

export const toBooleanFlag = (value, defaultValue = true) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") return true;
    if (normalized === "false") return false;
  }
  if (typeof value === "number") return value !== 0;
  return defaultValue;
};

export const toNumberOr = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

export const normalizeCardsFlags = (rawFlags) => {
  const parsedFlags = parseMaybeObject(rawFlags) || {};
  const parsedCards = parseMaybeObject(parsedFlags.cards);
  return parsedCards || null;
};

export const getCardsFlagsFromValue = (value) => {
  const root = parseMaybeObject(value) || {};
  const dataObj = parseMaybeObject(root.data) || {};
  const rootCards = parseMaybeObject(root.flags?.cards);
  if (rootCards) return rootCards;
  const nestedCards = parseMaybeObject(dataObj._flags?.cards);
  if (nestedCards) return nestedCards;
  return null;
};

export const isContentNode = (node) =>
  node &&
  typeof node === "object" &&
  Object.prototype.hasOwnProperty.call(node, "id") &&
  (Object.prototype.hasOwnProperty.call(node, "value") ||
    Object.prototype.hasOwnProperty.call(node, "mediaUrl") ||
    Object.prototype.hasOwnProperty.call(node, "isActive"));

export const isStructuredContentBlock = (node) =>
  node &&
  typeof node === "object" &&
  !Array.isArray(node) &&
  Object.prototype.hasOwnProperty.call(node, "data") &&
  typeof node.data === "object" &&
  node.data !== null &&
  !Array.isArray(node.data);

export const flattenContent = (input, path = [], rootSection = null) => {
  if (Array.isArray(input)) {
    return input.flatMap((item, index) => flattenContent(item, [...path, String(index)], rootSection));
  }
  if (!input || typeof input !== "object") return [];

  if (isContentNode(input)) {
    const label = input.label || input.key || path[path.length - 1] || `Item ${input.id}`;
    const sectionName =
      input.section ||
      input.sectionKey ||
      input.sectionName ||
      input.group ||
      input.groupKey ||
      input.category ||
      input.component ||
      rootSection ||
      "general";
    const itemPath = `${sectionName}.${input.key || label}`;
    return [
      {
        id: input.id,
        key: input.key || label,
        label,
        path: itemPath,
        sectionKey: sectionName,
        backendSection: sectionName,
        page: input.page || "",
        language: input.language || "",
        type: input.type || "text",
        order: Number.isFinite(Number(input.order)) ? Number(input.order) : 0,
        value: input.value ?? "",
        flags: null,
        isActive: Boolean(input.isActive),
        mediaUrl: input.mediaUrl || "",
      },
    ];
  }

  if (isStructuredContentBlock(input)) {
    const label = path[path.length - 1] || "Structured content";
    return [
      {
        id: input.id || input.contentId || input.entryId || "",
        key: label,
        label,
        path: path.join("."),
        sectionKey: rootSection || label || "general",
        backendSection:
          input.section ||
          input.sectionKey ||
          input.sectionName ||
          input.group ||
          input.groupKey ||
          input.category ||
          input.component ||
          "",
        value: input,
        flags: isPlainObject(input.flags) ? input.flags : parseMaybeObject(input.flags) || null,
        isActive: Boolean(input.isActive),
        mediaUrl: input.mediaUrl || "",
      },
    ];
  }

  return Object.entries(input).flatMap(([key, value]) => {
    const nextRootSection = rootSection || key;
    return flattenContent(value, [...path, key], nextRootSection);
  });
};

export const toSectionTitle = (sectionKey) =>
  String(sectionKey || "general")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const getSectionKey = ({ path, sectionKey, key, backendSection }) => {
  const normalizeCandidate = (candidate) => {
    const raw = String(candidate || "").trim();
    if (!raw) return "";
    const first = raw.split(".").find((part) => part && !/^\d+$/.test(part));
    if (!first) return "";
    return first.toLowerCase();
  };

  const fromBackend = normalizeCandidate(backendSection);
  if (fromBackend) return fromBackend;
  const fromSection = normalizeCandidate(sectionKey);
  if (fromSection) return fromSection;
  const fromPath = normalizeCandidate(path);
  if (fromPath) return fromPath;
  const fromKey = normalizeCandidate(key);
  if (fromKey) return fromKey;
  return "general";
};

export const normalizeLanguage = (lang) => {
  const candidate = String(lang || "en").toLowerCase();
  return languages.includes(candidate) ? candidate : "en";
};

const filePathLikeRegex = /^(\/?uploads\/|\.{1,2}\/|[a-zA-Z]:\\|\\|\/)/i;
const imageExtensionRegex = /\.(png|jpe?g|gif|webp|svg|avif|bmp)(\?.*)?$/i;
const videoExtensionRegex = /\.(mp4|mov|webm|avi|mkv|m4v)(\?.*)?$/i;
export const itemFieldRegex = /^item(\d+)([A-Z].+)$/;

export const detectMediaType = (url) => {
  if (!url) return "none";
  if (imageExtensionRegex.test(url)) return "image";
  if (videoExtensionRegex.test(url)) return "video";
  return "file";
};

export const findRepeatablePayload = (value, depth = 0) => {
  if (!isPlainObject(value) || depth > 3) return null;

  const dataObj = parseMaybeObject(value.data);
  const flagsObj = parseMaybeObject(value.flags);
  const cardsObj =
    parseMaybeObject(flagsObj?.cards) ||
    parseMaybeObject(dataObj?._flags?.cards) ||
    parseMaybeObject(value?._flags?.cards);

  if (dataObj && cardsObj && toBooleanFlag(cardsObj.isRepeatable, false)) {
    const sanitizedData = { ...dataObj };
    delete sanitizedData._flags;
    return {
      ...value,
      data: sanitizedData,
      flags: {
        ...(flagsObj || {}),
        cards: cardsObj,
      },
    };
  }

  const nested = Object.values(value)
    .map((entry) => parseMaybeObject(entry) || entry)
    .filter(isPlainObject);
  for (const entry of nested) {
    const found = findRepeatablePayload(entry, depth + 1);
    if (found) return found;
  }
  return null;
};

export const getCardsFromData = (dataObject) => {
  const cardsMap = new Map();
  Object.entries(dataObject || {}).forEach(([key, value]) => {
    const match = key.match(itemFieldRegex);
    if (!match) return;
    const index = Number(match[1]);
    const suffix = match[2];
    if (!cardsMap.has(index)) cardsMap.set(index, {});
    cardsMap.get(index)[suffix] = value ?? "";
  });
  return Array.from(cardsMap.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([index, fields]) => ({ index, fields }));
};
